/**
 * Utility functions for JWT token handling
 */

import {isOrganizationRole, isProviderRole, isSuperAdministratorRole} from '@mld/shared/constants/userRoles';

export type TokenPayload = {
    impersonate_id?: number | string | null;
    userId?: number | string | null;
    role_id?: number | string | null;
    type_id?: number | string | null;
    organization_id?: number | string | null;
    org_id?: number | string | null;
    org_subdomain?: string | null;
    permissions?: string[];
    exp?: number;
    iat?: number;
} & Record<string, unknown>;

function normalizeNullableId(value: number | string | null | undefined): string | null {
    if (value == null) return null;
    const normalized = String(value).trim();
    return normalized ? normalized : null;
}

export function getOrganizationIdFromTokenPayload(payload?: TokenPayload | null): string | null {
    // Tokens vary across auth issuers/versions; support both keys.
    return normalizeNullableId(payload?.organization_id) ?? normalizeNullableId(payload?.org_id);
}

/**
 * Decode JWT token payload
 */
export const decodeJwtToken = (token: string): TokenPayload | null => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(function (c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                })
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch {
        return null;
    }
};

/**
 * Check if user is admin based on token
 */
export const isUserAdmin = (token?: string): boolean => {
    if (!token) return false;

    const payload = decodeJwtToken(token);
    return Boolean(isSuperAdministratorRole(payload?.role_id) && !payload?.impersonate_id);
};

/**
 * Returns true if the token represents a provider, organization, or super-admin
 * user with a valid type_id.  Used by the router to distinguish provider/org
 * console users from patients.
 */
export function isOrganizationUser(token?: string | null): boolean {
    if (!token) return false;
    const payload = decodeJwtToken(token);
    return isOrganizationRole(payload?.role_id);
}

export function isProviderOrOrganizationUser(token?: string | null): boolean {
    if (!token) return false;
    const payload = decodeJwtToken(token);
    const roleId = payload?.role_id;
    const typeId = payload?.type_id;
    const hasTypeId = typeId != null && String(typeId).trim() !== '';
    return (isProviderRole(roleId) || isOrganizationRole(roleId) || isSuperAdministratorRole(roleId)) && hasTypeId;
}

/**
 * Sessions issued by an external launch (currently only OHD's patient
 * portal) carry `launch_source` + `launch_return_url` claims so the
 * dashboard can show a context-appropriate "Reschedule" CTA. Returns
 * null when the session wasn't externally launched.
 */
export function getLaunchContext(
    token?: string | null
): {source: 'ohd'; returnUrl?: string} | null {
    if (!token) return null;
    const payload = decodeJwtToken(token);
    if (payload?.launch_source !== 'ohd') return null;
    const returnUrl =
        typeof payload.launch_return_url === 'string' && payload.launch_return_url.trim().length > 0
            ? payload.launch_return_url.trim()
            : undefined;
    return {source: 'ohd', returnUrl};
}
